import { EVENT_DELAY } from '@src/config/constants'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { initializeScript } from '@src/telemetry/integrations/provider-utils/initialize-script'
import { TelemetryIntegration } from '@src/telemetry/telemetry-events.types'
import { getStorefrontIdProperty } from '@src/utils/b2b'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'

import { TELEMETRY_DOMAINS } from '../telemetry-domains.constants'
import { TELEMETRY_EVENT_NAMES } from '../telemetry-events.constants'

export const IMPACT_CLICK_ID_URL_SEARCH_PARAM_KEY = 'irclickid'
export const IMPACT_CLICK_ID_COOKIE_EXPIRY_IN_MINUTES = 43200 // 30 days

export const trackImpactConversion = async (
  actionId: string,
  properties: { eventId: string; placing?: 'popup' | 'Deal_Page' | 'footer' },
) => {
  const email = await AuthUtil.getUserHashedEmail()
  const lbUserId = getlbUserId() ?? ''
  const localisationData = getLocalisationDataFromStorage()
  executeFireAndForgotTask(() => {
    if (typeof window.ire === 'function') {
      window.ire('trackConversion', actionId, {
        orderId: properties.eventId,
        customerId: email,
        customerEmail: email,
        customProfileId: lbUserId,
        locationType: properties.placing,
        currency: localisationData?.currency,
        country: localisationData?.country,
        ...getStorefrontIdProperty(), //storefront_id is used to identify the b2b self serve flow
      })
    }
  }, EVENT_DELAY)
}

export const Impact: TelemetryIntegration = {
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
    domain: TELEMETRY_DOMAINS.IMPACT,
    featureGateName: 'ssr_telemetry_impact',
  },
  initialize: async () => {
    await initializeScript({
      key: 'tracking-provider-impact',
      domain: TELEMETRY_DOMAINS.IMPACT,
      path: '/A4911671-b2f3-4e66-bd44-77c54a2656441.js',
      injectAt: 'beforeFirstScript',
      maxDelay: 1000,
      init: () => {
        window.ire_o = 'ire'
        window.ire =
          window.ire ??
          function (...args: unknown[]) {
            if (window.ire) {
              ;(window.ire.a = window.ire.a ?? []).push(args)
            }
          }
      },
    })
  },
  events: {
    // Note: Not handling page view event here explicitly, this is because fb pixel script takes care
    // of triggering Page View event in case of browser history state change.
    //  https://developers.facebook.com/ads/blog/post/v2/2017/05/29/tagging-a-single-page-application-facebook-pixel/

    [TELEMETRY_EVENT_NAMES.PAGE_VIEW]: async properties => {
      const email = AuthUtil.isLoggedIn() ? await AuthUtil.getUserHashedEmail() : ''
      const lbUserId = getlbUserId() ?? ''
      const localisationData = getLocalisationDataFromStorage()
      executeFireAndForgotTask(() => {
        if (typeof window.ire !== 'function') return
        window.ire('identify', {
          orderId: properties.eventId,
          customerId: email,
          customerEmail: email,
          customProfileId: lbUserId,
          currency: localisationData?.currency,
          country: localisationData?.country,
          ...getStorefrontIdProperty(), //storefront_id is used to identify the b2b self serve flow
        })
      }, EVENT_DELAY)
    },

    [TELEMETRY_EVENT_NAMES.PRODUCT_VIEW]: async properties => {
      if (productHelper.isGiftCard(properties.productId)) {
        await trackImpactConversion('41394', properties)
      }
    },

    [TELEMETRY_EVENT_NAMES.EMAIL_NEWSLETTER_SUBSCRIBE]: async properties => {
      await trackImpactConversion('41391', properties)
    },

    [TELEMETRY_EVENT_NAMES.SEND_REFERRAL_EMAIL_INVITE]: async properties => {
      await trackImpactConversion('41392', properties)
    },

    [TELEMETRY_EVENT_NAMES.MAIL_TO_US_CLICKED]: async properties => {
      await trackImpactConversion('41393', properties)
    },

    [TELEMETRY_EVENT_NAMES.CHAT_WITH_US_CLICKED]: async properties => {
      await trackImpactConversion('41396', properties)
    },

    [TELEMETRY_EVENT_NAMES.PHONE_TO_US_CLICKED]: async properties => {
      await trackImpactConversion('41395', properties)
    },

    // Note: Below is disabled as per business ask.
    // Keeping it commented to enable it back as is
    // [EventType.CART_VIEW] = async (properties: any) => {
    //   trackImpactConversion('41397', properties);
    // };

    [TELEMETRY_EVENT_NAMES.SMS_NEWSLETTER_SUBSCRIBE]: async properties => {
      await trackImpactConversion('41435', properties)
    },
  },
}
